/**
 * Pure layout core for the calendar time grid (Packet 6 Lane C, docs/44
 * §3.2). No React, no DOM, no network, no clock: `nowMs` is an input and the
 * module never constructs a Date or consults a locale (layout.test.ts scans
 * the source to keep it that way; `Date.UTC` is pure calendar arithmetic and
 * the one allowed exception).
 *
 * Positions are FRACTIONS of the rendered span — jsdom has no layout and the
 * test-suite ResizeObserver stub never fires, so anything pixel-measured
 * renders nothing under test (the BackfillTimeline precedent). A 23-hour
 * spring-forward day divides by 23 hours and a 25-hour fall-back day by 25:
 * the day's true length is always `endMs - startMs` from the server's
 * `days[]`, never 24 hours.
 */

import {
  CalendarEntryKind,
  DayBounds,
  DaySegment,
  DaySplit,
  HourMarkLike,
  LayoutEntry,
  LayoutOptions,
  PackedDayDual,
  PositionedEntry,
  WeekStart,
} from './layoutTypes';

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

/** Cosmetic floor for near-zero blocks when options omit one. */
export const DEFAULT_MIN_BLOCK_MINUTES = 12;

/** Plan-side kinds for the "Compare plan vs actual" split (D-P6-2). */
export const PLAN_KINDS: ReadonlyArray<CalendarEntryKind> = [
  'commitment_occurrence',
  'planned_event',
  'task_block',
];

/**
 * Strict overlap on half-open spans. A zero-duration span [t, t) is
 * mathematically empty, so it overlaps only spans that STRICTLY contain t —
 * a marker sitting exactly at another block's start shares its column, which
 * is what "the min-height clamp must not cause a false overlap" means:
 * packing uses true intervals and the drawn height is cosmetic. The one
 * deliberate exception: two zero-duration markers at the same instant count
 * as overlapping so they split columns instead of overpainting each other.
 */
const spansOverlap = (
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): boolean => {
  if (aStart === aEnd && bStart === bEnd) {
    return aStart === bStart;
  }
  return aStart < bEnd && bStart < aEnd;
};

interface Span {
  start: number;
  end: number;
  isGhost: boolean;
  isLiveEdge: boolean;
}

/**
 * A wire entry becomes 1–2 drawable spans: the real span (a running entry is
 * clamped to `nowMs` and carries the live edge) plus, for a running entry
 * whose expected_end is still ahead, a ghost span nowMs→expected_end. Ghosts
 * are never emitted for closed entries. Inverted spans (end before start —
 * the server-side `_obstacles` skips these too) yield nothing rather than a
 * negative box.
 */
const materialize = (entry: LayoutEntry, nowMs: number): Span[] => {
  if (entry.endMs !== null && entry.endMs !== undefined) {
    if (entry.endMs < entry.startMs) {
      return [];
    }
    return [
      { start: entry.startMs, end: entry.endMs, isGhost: false, isLiveEdge: false },
    ];
  }
  if (entry.kind !== 'time_entry') {
    // An open end is a running clock only for logged time. Any other kind
    // with no end (an open-ended imported event) is a fact with no duration:
    // a zero-duration marker, never a start-to-now span, never a live edge —
    // the Wave 3 "fabricated running block" finding.
    return [
      { start: entry.startMs, end: entry.startMs, isGhost: false, isLiveEdge: false },
    ];
  }
  const realEnd = Math.max(entry.startMs, nowMs);
  const spans: Span[] = [
    { start: entry.startMs, end: realEnd, isGhost: false, isLiveEdge: true },
  ];
  if (
    entry.expectedEndMs !== null &&
    entry.expectedEndMs !== undefined &&
    entry.expectedEndMs > realEnd
  ) {
    spans.push({ start: realEnd, end: entry.expectedEndMs, isGhost: true, isLiveEdge: false });
  }
  return spans;
};

/**
 * Clamp an entry to a half-open window of instants, or drop it entirely.
 * Used for the visible-hours window, and by `splitAcrossDays`/`packDay` for
 * day edges — one clipping branch, so a defect here is caught everywhere.
 *
 * Returns a NEW object (inputs are never mutated); `null` when the entry is
 * wholly outside. A zero-duration entry survives while its instant is inside
 * the half-open window. An unbounded end (`endMs === null`) is treated as
 * extending past any window — prefer materialising running entries first so
 * the live edge lands at `nowMs`, not at the window edge.
 */
export const clampToWindow = <T extends LayoutEntry>(
  entry: T,
  windowStartMs: number,
  windowEndMs: number,
): T | null => {
  const start = entry.startMs;
  const end = entry.endMs === null || entry.endMs === undefined ? Infinity : entry.endMs;
  if (start === end) {
    if (start < windowStartMs || start >= windowEndMs) {
      return null;
    }
    return {
      ...entry,
      clippedStart: Boolean(entry.clippedStart),
      clippedEnd: Boolean(entry.clippedEnd),
    };
  }
  if (start >= windowEndMs || end <= windowStartMs) {
    return null;
  }
  const clipStart = start < windowStartMs;
  const clipEnd = end > windowEndMs;
  return {
    ...entry,
    startMs: clipStart ? windowStartMs : start,
    endMs: clipEnd ? windowEndMs : (entry.endMs as number),
    clippedStart: clipStart || Boolean(entry.clippedStart),
    clippedEnd: clipEnd || Boolean(entry.clippedEnd),
  };
};

/**
 * Wall-clock visible-window edges for one day, derived from the server's own
 * hour marks so a DST day's "06:00" really is 06:00 on that day's wall. A
 * minute that is not a whole hour resolves as its hour's mark plus the
 * remainder. A doubled fall-back label resolves to its FIRST occurrence
 * (fold 0 — chronological, matching the server's ordering); a label missing
 * entirely (the spring-forward skipped hour) falls back to elapsed-minute
 * arithmetic from the day's open. `null` minutes mean the day's own edge.
 */
export const resolveWindowEdges = (
  bounds: DayBounds,
  marks: ReadonlyArray<HourMarkLike>,
  startMinute: number | null,
  endMinute: number | null,
): { startMs: number; endMs: number } => {
  const edgeFor = (minute: number): number => {
    const hour = Math.floor(minute / 60);
    const remainder = minute % 60;
    const label = `${String(hour).padStart(2, '0')}:00`;
    const mark = marks.find((candidate) => candidate.label === label);
    if (mark) {
      return mark.atMs + remainder * MINUTE_MS;
    }
    return bounds.startMs + minute * MINUTE_MS;
  };
  const startMs = startMinute === null ? bounds.startMs : edgeFor(startMinute);
  const endMs = endMinute === null ? bounds.endMs : edgeFor(endMinute);
  return { startMs, endMs: Math.min(endMs, bounds.endMs) };
};

/** Days sorted chronologically; the input array is left untouched. */
export const orderDays = (days: ReadonlyArray<DayBounds>): DayBounds[] =>
  [...days].sort((a, b) => a.startMs - b.startMs);

/**
 * Weekday of a YYYY-MM-DD label, Sunday = 0. Pure calendar arithmetic on the
 * labeled date (1970-01-01 was a Thursday): no zone, no clock, no Date
 * construction — the label names a local date and its weekday is a property
 * of the label, not of any timezone.
 */
const epochDayOfDateKey = (dateKey: string): number => {
  const [year, month, dayOfMonth] = dateKey.split('-').map(Number);
  return Date.UTC(year, month - 1, dayOfMonth) / DAY_MS;
};

const weekdayOfDateKey = (dateKey: string): number => {
  const epochDays = epochDayOfDateKey(dateKey);
  return (((epochDays + 4) % 7) + 7) % 7;
};

/**
 * Chunk chronological days into week rows, breaking before each week-start
 * day. Chronological order is the same under either week start; only the row
 * boundaries move.
 */
export const groupIntoWeeks = (
  days: ReadonlyArray<DayBounds>,
  weekStart: WeekStart,
): DayBounds[][] => {
  const boundary = weekStart === 'sunday' ? 0 : 1;
  const weeks: DayBounds[][] = [];
  let current: DayBounds[] = [];
  for (const day of orderDays(days)) {
    if (current.length > 0 && weekdayOfDateKey(day.dateKey) === boundary) {
      weeks.push(current);
      current = [];
    }
    current.push(day);
  }
  if (current.length > 0) {
    weeks.push(current);
  }
  return weeks;
};

/**
 * Split entries into per-day segments for rendering — a view artefact only:
 * every segment keeps the source id and the inputs are never mutated (the
 * suite passes frozen objects to prove it). All-day entries route to the
 * all-day rail of every day they touch and never into the grid; an all-day
 * entry without an end is treated as single-date rather than unbounded.
 * `nowMs` materialises running entries (their extent ends at now, with the
 * ghost continuing to expected_end).
 */
export const splitAcrossDays = (
  entries: ReadonlyArray<LayoutEntry>,
  days: ReadonlyArray<DayBounds>,
  nowMs: number,
): Map<string, DaySplit> => {
  const ordered = orderDays(days);
  const result = new Map<string, DaySplit>();
  ordered.forEach((day) => result.set(day.dateKey, { grid: [], allDayRail: [] }));

  for (const entry of entries) {
    const drafts: Array<{ dateKey: string; segment: DaySegment }> = [];
    if (entry.allDay) {
      // An all-day entry is a DATE (or date range), not a span of minutes:
      // both import paths stamp it as a zone-agnostic UTC midnight, so its
      // calendar date is its UTC date, matched against each day's dateKey —
      // never intersected with local-day instants, which put a Mar 10
      // birthday on Mar 9 in every zone west of UTC (Wave 3 finding).
      const firstDay = Math.floor(entry.startMs / DAY_MS);
      const lastDay =
        entry.endMs === null || entry.endMs === undefined || entry.endMs <= entry.startMs
          ? firstDay
          : Math.floor((entry.endMs - 1) / DAY_MS);
      for (const day of ordered) {
        const dayEpoch = epochDayOfDateKey(day.dateKey);
        if (dayEpoch < firstDay || dayEpoch > lastDay) {
          continue;
        }
        drafts.push({
          dateKey: day.dateKey,
          segment: {
            ...entry,
            endMs:
              entry.endMs === null || entry.endMs === undefined
                ? entry.startMs
                : entry.endMs,
            clippedStart: Boolean(entry.clippedStart),
            clippedEnd: Boolean(entry.clippedEnd),
            sourceId: entry.id,
            segmentIndex: 0,
            segmentCount: 0,
            isGhost: false,
            isLiveEdge: false,
          },
        });
      }
    } else {
      for (const span of materialize(entry, nowMs)) {
        for (const day of ordered) {
          const clamped = clampToWindow(
            { ...entry, startMs: span.start, endMs: span.end },
            day.startMs,
            day.endMs,
          );
          if (!clamped) {
            continue;
          }
          if (span.isGhost && clamped.startMs === clamped.endMs) {
            continue;
          }
          drafts.push({
            dateKey: day.dateKey,
            segment: {
              ...clamped,
              sourceId: entry.id,
              segmentIndex: 0,
              segmentCount: 0,
              isGhost: span.isGhost,
              // The live edge belongs to the segment whose bottom IS "now" —
              // a day-clipped earlier segment continues, it does not run.
              isLiveEdge: span.isLiveEdge && clamped.endMs === span.end,
            },
          });
        }
      }
    }
    drafts.sort(
      (a, b) =>
        a.segment.startMs - b.segment.startMs ||
        Number(a.segment.isGhost) - Number(b.segment.isGhost),
    );
    drafts.forEach((draft, index) => {
      draft.segment.segmentIndex = index;
      draft.segment.segmentCount = drafts.length;
      const bucket = result.get(draft.dateKey) as DaySplit;
      (entry.allDay ? bucket.allDayRail : bucket.grid).push(draft.segment);
    });
  }
  return result;
};

interface Draft {
  entry: LayoutEntry;
  sourceId: string;
  start: number;
  end: number;
  clippedStart: boolean;
  clippedEnd: boolean;
  isGhost: boolean;
  isLiveEdge: boolean;
  segmentIndex?: number;
  segmentCount?: number;
  column: number;
  columnCount: number;
}

/**
 * Position one day's entries as fractional boxes.
 *
 * Packing: entries sort by (start, longer-first, source id) for determinism;
 * overlap clusters are the transitive closure of pairwise `spansOverlap`
 * (chain A–B, B–C with A∦C is one cluster and A and C may share a column);
 * within a cluster each entry takes the first column free of overlap, and
 * `columnCount` is the cluster's column total — which, for start-sorted
 * greedy assignment over intervals, equals the maximum concurrency.
 *
 * Ghosts never participate in packing: each inherits its parent's column (or
 * column 0 when the parent's real segment is on another day), so a ghost can
 * overpaint a neighbour but can never squeeze one narrower.
 *
 * Accepts raw entries (running entries are materialised against
 * `options.nowMs`; all-day entries are skipped — the rail is their only
 * home) or pre-split `DaySegment`s (recognised by their `isGhost` field, and
 * their segment numbering is preserved). When a visible window is set,
 * fractions are relative to the window span, since that is what the column
 * renders.
 */
export const packDay = (
  entries: ReadonlyArray<LayoutEntry | DaySegment>,
  dayBounds: DayBounds,
  options: LayoutOptions,
): PositionedEntry[] => {
  const renderStart =
    options.windowStartMs !== undefined
      ? options.windowStartMs
      : options.windowStartMinute !== undefined
        ? dayBounds.startMs + options.windowStartMinute * MINUTE_MS
        : dayBounds.startMs;
  const renderEnd =
    options.windowEndMs !== undefined
      ? options.windowEndMs
      : options.windowEndMinute !== undefined
        ? dayBounds.startMs + options.windowEndMinute * MINUTE_MS
        : dayBounds.endMs;
  const renderSpan = renderEnd - renderStart;
  if (renderSpan <= 0) {
    return [];
  }
  const minFraction = Math.min(
    1,
    ((options.minBlockMinutes ?? DEFAULT_MIN_BLOCK_MINUTES) * MINUTE_MS) / renderSpan,
  );

  const reals: Draft[] = [];
  const ghosts: Draft[] = [];
  for (const raw of entries) {
    if (raw.allDay) {
      continue;
    }
    const seg = raw as Partial<DaySegment> & LayoutEntry;
    const sourceId = seg.sourceId ?? raw.id;
    const preMaterialized =
      seg.isGhost !== undefined && raw.endMs !== null && raw.endMs !== undefined;
    const spans: Span[] = preMaterialized
      ? [
          {
            start: raw.startMs,
            end: raw.endMs as number,
            isGhost: Boolean(seg.isGhost),
            isLiveEdge: Boolean(seg.isLiveEdge),
          },
        ]
      : materialize(raw, options.nowMs);
    for (const span of spans) {
      const clamped = clampToWindow(
        { ...raw, startMs: span.start, endMs: span.end },
        renderStart,
        renderEnd,
      );
      if (!clamped) {
        continue;
      }
      if (span.isGhost && clamped.startMs === clamped.endMs) {
        continue;
      }
      const draft: Draft = {
        entry: raw,
        sourceId,
        start: clamped.startMs,
        end: clamped.endMs as number,
        clippedStart: Boolean(clamped.clippedStart),
        clippedEnd: Boolean(clamped.clippedEnd),
        isGhost: span.isGhost,
        isLiveEdge: span.isLiveEdge && clamped.endMs === span.end,
        segmentIndex: seg.segmentIndex,
        segmentCount: seg.segmentCount,
        column: 0,
        columnCount: 1,
      };
      (span.isGhost ? ghosts : reals).push(draft);
    }
  }

  reals.sort(
    (a, b) =>
      a.start - b.start ||
      b.end - b.start - (a.end - a.start) ||
      (a.sourceId < b.sourceId ? -1 : a.sourceId > b.sourceId ? 1 : 0),
  );

  // Transitive overlap clusters via union-find over the pairwise predicate.
  // Exact adjacency semantics beat a start-sweep here because zero-duration
  // spans have empty intervals with one special-cased pairing.
  const parent = reals.map((_, index) => index);
  const findRoot = (index: number): number => {
    let root = index;
    while (parent[root] !== root) {
      root = parent[root];
    }
    let cursor = index;
    while (parent[cursor] !== root) {
      const next = parent[cursor];
      parent[cursor] = root;
      cursor = next;
    }
    return root;
  };
  for (let i = 0; i < reals.length; i += 1) {
    for (let j = i + 1; j < reals.length; j += 1) {
      if (spansOverlap(reals[i].start, reals[i].end, reals[j].start, reals[j].end)) {
        parent[findRoot(i)] = findRoot(j);
      }
    }
  }
  const clusters = new Map<number, number[]>();
  reals.forEach((_, index) => {
    const root = findRoot(index);
    const members = clusters.get(root);
    if (members) {
      members.push(index);
    } else {
      clusters.set(root, [index]);
    }
  });
  clusters.forEach((members) => {
    let used = 1;
    members.forEach((i) => {
      let column = 0;
      const collides = (candidate: number): boolean =>
        members.some(
          (j) =>
            j < i &&
            reals[j].column === candidate &&
            spansOverlap(reals[i].start, reals[i].end, reals[j].start, reals[j].end),
        );
      while (collides(column)) {
        column += 1;
      }
      reals[i].column = column;
      used = Math.max(used, column + 1);
    });
    members.forEach((i) => {
      reals[i].columnCount = used;
    });
  });

  const firstRealBySource = new Map<string, Draft>();
  reals.forEach((draft) => {
    if (!firstRealBySource.has(draft.sourceId)) {
      firstRealBySource.set(draft.sourceId, draft);
    }
  });
  ghosts.forEach((ghost) => {
    const parentDraft = firstRealBySource.get(ghost.sourceId);
    ghost.column = parentDraft ? parentDraft.column : 0;
    ghost.columnCount = parentDraft ? parentDraft.columnCount : 1;
  });

  // Segment numbering: preserved from splitAcrossDays when present, else
  // assigned day-locally per source entry in chronological order.
  const unnumbered = new Map<string, Draft[]>();
  [...reals, ...ghosts].forEach((draft) => {
    if (draft.segmentIndex !== undefined && draft.segmentCount !== undefined) {
      return;
    }
    const list = unnumbered.get(draft.sourceId);
    if (list) {
      list.push(draft);
    } else {
      unnumbered.set(draft.sourceId, [draft]);
    }
  });
  unnumbered.forEach((list) => {
    list.sort((a, b) => a.start - b.start || Number(a.isGhost) - Number(b.isGhost));
    list.forEach((draft, index) => {
      draft.segmentIndex = index;
      draft.segmentCount = list.length;
    });
  });

  const position = (draft: Draft): PositionedEntry => {
    // A live-edged [start, start] span is a running entry seen by a clock
    // that lags its start — still running, never a Tasks-style marker.
    const isZeroDuration =
      draft.end === draft.start && !draft.isGhost && !draft.isLiveEdge;
    let topFraction = (draft.start - renderStart) / renderSpan;
    let heightFraction = (draft.end - draft.start) / renderSpan;
    if (heightFraction < minFraction) {
      heightFraction = minFraction;
      topFraction = Math.max(0, Math.min(topFraction, 1 - heightFraction));
    }
    return {
      sourceId: draft.sourceId,
      kind: draft.entry.kind,
      column: draft.column,
      columnCount: draft.columnCount,
      topFraction,
      heightFraction,
      clippedStart: draft.clippedStart,
      clippedEnd: draft.clippedEnd,
      isAllDay: false,
      isZeroDuration,
      isLiveEdge: draft.isLiveEdge,
      isGhost: draft.isGhost,
      segmentIndex: draft.segmentIndex ?? 0,
      segmentCount: draft.segmentCount ?? 1,
      spanStartMs: draft.start,
      spanEndMs: draft.end,
    };
  };
  return [...reals.map(position), ...ghosts.map(position)];
};

/**
 * The "Compare plan vs actual" split: plan kinds (commitments, imported
 * events, task blocks) and actual (time entries) pack independently — the
 * same core run twice, so the toggle costs the page nothing.
 */
export const packDayDual = (
  entries: ReadonlyArray<LayoutEntry | DaySegment>,
  dayBounds: DayBounds,
  options: LayoutOptions,
): PackedDayDual => ({
  plan: packDay(
    entries.filter((entry) => entry.kind !== 'time_entry'),
    dayBounds,
    options,
  ),
  actual: packDay(
    entries.filter((entry) => entry.kind === 'time_entry'),
    dayBounds,
    options,
  ),
});
